import { Badge, Button, FormatDate, Icon, Modal, Text, Tooltip } from '@wyz/ui';
import { DateUtils, OFFER_TYPE } from '@wyz/utils';
import { TextField } from '~/common/components';
import { Trans, useI18n, useLocale } from '~/common/context';
import { IAcceptOrderItemPayload, ISuggestedOffer } from '~/purchase/types';

type Props = {
  offers: ISuggestedOffer[];
  watchedItems: IAcceptOrderItemPayload[];
  onConfirm: () => void;
  onClose: () => void;
};

const ConfirmOrderModal = ({
  offers,
  watchedItems,
  onConfirm,
  onClose,
}: Props) => {
  const { t } = useI18n();
  const locale = useLocale();

  const getQuantity = (suggestedOffer: ISuggestedOffer) => {
    return (
      watchedItems.find((item) => item.lineId === suggestedOffer.offerId)
        ?.quantity ?? 0
    );
  };

  const hasOrderableSuppliers = offers.some(
    (suggestedOffer) =>
      suggestedOffer.offer.supplier.orderable === true &&
      suggestedOffer.offer.offerType !== OFFER_TYPE.DISTRIBUTOR_PLATFORM,
  );

  return (
    <Modal.Container open backdrop='static' centered onClose={onClose}>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.purchase.redistribution.not_orderable_orders.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='w-100 bg-white'>
          <Text heading='xs' weight='normal' className='mb-4'>
            {!hasOrderableSuppliers
              ? t('app.purchase.redistribution.not_orderable_orders.text')
              : t('app.purchase.redistribution.orderable_orders.text')}
          </Text>
        </div>
        <div className='bg-neutral-grey-2 p-1 mb-2'>
          <table>
            <thead>
              <th className='text-center p-2 col-3'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.supplier' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.quantity' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.orders.delivery_date' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.buying_pricing_unit' />
                </Text>
              </th>
              <th className='p-2 text-center col-4'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.status' />
                </Text>
              </th>
            </thead>
            <tbody>
              {offers
                .filter(
                  (suggestedOffer) =>
                    suggestedOffer.offer.offerType !==
                    OFFER_TYPE.DISTRIBUTOR_PLATFORM,
                )
                .map((suggestedOffer, offerIndex) => {
                  const quantity = getQuantity(suggestedOffer);
                  if (!quantity) {
                    return <></>;
                  }
                  return (
                    <tr key={offerIndex}>
                      <td className='text-center p-1 no-border'>
                        <Text
                          heading='xs'
                          weight='semi-bold'
                          className=' shadow-none border-0 '
                          id={`supplier-${suggestedOffer.offer.supplier.name}`}
                        >
                          {suggestedOffer.offer.supplier.name}
                        </Text>
                      </td>
                      <td className='text-center p-1 no-border'>
                        <Text
                          heading='xs'
                          weight='semi-bold'
                          className=' shadow-none border-0 '
                          id={`supplier-${suggestedOffer.offer.supplier.name}-quantity`}
                        >
                          {quantity}
                        </Text>
                      </td>
                      <td className='text-center p-2 no-border'>
                        <Text
                          id={`${offerIndex}-${suggestedOffer.offer.supplier.name}-delivery`}
                          heading='xs'
                          className=' shadow-none border-0 '
                          weight='semi-bold'
                        >
                          {suggestedOffer.offer.isBackOrder ? (
                            t('app.common.offer_type.backorder')
                          ) : (
                            <FormatDate
                              date={suggestedOffer.offer.deliveryDate}
                              format={DateUtils.DATE_FORMAT.DM}
                              locale={locale.locale}
                            />
                          )}
                        </Text>
                      </td>
                      <td className='text-center p-1 no-border'>
                        <Text
                          heading='xs'
                          weight='semi-bold'
                          className=' shadow-none border-0 '
                          id={`${offerIndex}-${suggestedOffer.offer.supplier.name}-priceC`}
                        >
                          {locale.formatters.money(
                            suggestedOffer.offer.redistributionBuyingPrice,
                            {
                              fallback: '-',
                              currency: suggestedOffer.offer.currencyIsoCode,
                            },
                          )}
                        </Text>
                      </td>
                      <td className='text-center p-2 no-border'>
                        <Text
                          heading='xs'
                          weight='semi-bold'
                          className=' shadow-none border-0 '
                          id={`supplier-${suggestedOffer.offer.supplier.name}-status`}
                        >
                          <Badge
                            bg={
                              suggestedOffer.offer.supplier.orderable === false
                                ? 'warning'
                                : 'success'
                            }
                            pill
                            className='text-xs'
                          >
                            <Trans
                              i18nKey={
                                suggestedOffer.offer.supplier.orderable ===
                                false
                                  ? 'app.common.status.not_orderable'
                                  : 'app.common.status.orderable'
                              }
                            />
                          </Badge>
                          {suggestedOffer.offer.supplier.orderable ===
                            false && (
                            <Tooltip
                              label={
                                <Trans i18nKey='app.purchase.redistribution.pendin_order.accept.not_orderable_tooltip' />
                              }
                              style={{ maxWidth: '300px' }}
                            >
                              <Text component='span' color='danger'>
                                <Icon icon='error-warning-line' />
                              </Text>
                            </Tooltip>
                          )}
                        </Text>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {hasOrderableSuppliers && (
          <div className='bg-neutral-grey-2 p-1'>
            <div className='p-2'>
              <TextField
                name='comment'
                type='text'
                as='textarea'
                label={<Trans i18nKey='app.common.comment' />}
                placeholder={t(
                  'app.purchase.orders.pending.confirm_modal.comment.placeholder',
                )}
                rows={3}
              />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!hasOrderableSuppliers ? (
          <Button
            id='i-understand-modal-btn'
            variant='primary'
            onClick={onConfirm}
            fullWidth
          >
            <Trans i18nKey='app.common.button.i_understand' />
          </Button>
        ) : (
          <Button
            id='i-accept-and-order-modal-btn'
            variant='primary'
            onClick={onConfirm}
            fullWidth
          >
            <Trans i18nKey='app.purchase.redistribution.pending_order.accept_with_order_modal_btn' />
          </Button>
        )}
      </Modal.Footer>
    </Modal.Container>
  );
};

export default ConfirmOrderModal;
